import type { AgBarSeriesOptions } from 'ag-charts-enterprise';
import type { ColDef, ColGroupDef } from 'ag-grid-enterprise';

import {
  PRIORITY_COLORS,
  REPORTS_COLORS
} from '@/constants/chartcolors.constants';
import theme from '@/theme';

export const CASES_REPORTS_VERTICAL_BAR_SERIES: AgBarSeriesOptions[] = [
  {
    type: 'bar',
    xKey: 'date',
    yKey: 'cases',
    yName: 'Cases',
    fill: REPORTS_COLORS.CASES
  },
  {
    type: 'bar',
    xKey: 'date',
    yKey: 'ticketedCases',
    yName: 'Ticketed Cases',
    yKeyAxis: 'ySecondary',
    fill: REPORTS_COLORS.INVESTIGATED_CASES
  }
];

export const TICKETED_LOG_CASES_REPORTS_VERTICAL_BAR_SERIES: AgBarSeriesOptions[] =
  [
    {
      type: 'bar',
      xKey: 'date',
      yKey: 'count',
      yName: 'Ticketed Cases',
      fill: REPORTS_COLORS.TICKETED_CASES
    }
  ];

const DEFAULT_REPORTS_HORIZONTAL_BAR_SERIES: AgBarSeriesOptions = {
  type: 'bar',
  xKey: 'name',
  xName: 'Category',
  yKey: 'count',
  yName: 'Counts',
  direction: 'horizontal',
  label: {
    enabled: true,
    formatter: ({ datum }) => {
      return datum.count;
    },
    placement: 'outside-end',
    fontSize: 8,
    color: 'black'
  }
};

export const REPORTS_SERVICE_TICKETS_HORIZONTAL_BAR_SERIES: AgBarSeriesOptions[] =
  [
    {
      ...DEFAULT_REPORTS_HORIZONTAL_BAR_SERIES,
      fill: '#008ffb'
    }
  ];

export const REPORTS_TICKETS_HORIZONTAL_BAR_SERIES: AgBarSeriesOptions[] = [
  {
    ...DEFAULT_REPORTS_HORIZONTAL_BAR_SERIES,
    fill: REPORTS_COLORS.TICKETS
  }
];

export const REPORTS_EXEC_TICKETS_HORIZONTAL_BAR_SERIES: AgBarSeriesOptions[] =
  [
    {
      ...DEFAULT_REPORTS_HORIZONTAL_BAR_SERIES,
      fill: '#075B64'
    }
  ];

export const REPORTS_NETWORK_EVENTS_HORIZONTAL_BAR_SERIES: AgBarSeriesOptions[] =
  [
    {
      ...DEFAULT_REPORTS_HORIZONTAL_BAR_SERIES,
      fill: REPORTS_COLORS.NETWORK_EVENTS
    }
  ];

export const REPORTS_LOG_EVENTS_HORIZONTAL_BAR_SERIES: AgBarSeriesOptions[] = [
  {
    ...DEFAULT_REPORTS_HORIZONTAL_BAR_SERIES,
    fill: REPORTS_COLORS.LOG_EVENTS
  }
];

export const REPORTS_ALERTS_HORIZONTAL_BAR_SERIES: AgBarSeriesOptions[] = [
  {
    ...DEFAULT_REPORTS_HORIZONTAL_BAR_SERIES,
    fill: REPORTS_COLORS.CASES
  }
];

export const CASES_RADIAL_BAR_SERIES = [
  // {
  //   type: 'radial-bar' as const,
  //   radiusKey: 'name',
  //   angleKey: 'investigatedAlerts',
  //   angleName: 'Investigated Alerts',
  //   stacked: true,
  //   fill: REPORTS_COLORS.INVESTIGATED_ALERTS
  // },
  {
    type: 'radial-bar' as const,
    radiusKey: 'name',
    angleKey: 'ticketedCases',
    angleName: 'Ticketed Cases',
    stacked: true,
    fill: REPORTS_COLORS.TICKETED_CASES
  },
  {
    type: 'radial-bar' as const,
    radiusKey: 'name',
    angleKey: 'remainder',
    angleName: '',
    stacked: true,
    fill: theme.palette.grey[400],
    cornerRadius: 18,
    highlight: {
      enabled: false
    },
    tooltip: {
      enabled: false
    }
  }
];

export const EDR_CASES_RADIAL_BAR_SERIES = {
  type: 'radial-bar' as const,
  radiusKey: 'name',
  angleKey: 'edrCases',
  angleName: 'EDR Cases',
  stacked: true,
  fill: '#8BB9BD'
};

export const NETWORK_CASES_RADIAL_BAR_SERIES = {
  type: 'radial-bar' as const,
  radiusKey: 'name',
  angleKey: 'networkCases',
  angleName: 'Network Cases',
  stacked: true,
  fill: REPORTS_COLORS.NETWORK_EVENTS
};

export const LOG_CASES_RADIAL_BAR_SERIES = {
  type: 'radial-bar' as const,
  radiusKey: 'name',
  angleKey: 'logCases',
  angleName: 'Log Cases',
  stacked: true,
  fill: REPORTS_COLORS.LOG_EVENTS
};

export const REPORTS_METRICS_COLUMNS: (ColDef | ColGroupDef)[] = [
  {
    field: 'group',
    spanRows: true,
    width: 50,
    headerName: '',
    resizable: false,
    suppressHeaderMenuButton: true,
    cellStyle: (params) => {
      const ponduranceRow = params?.value === 'Pondurance';

      return {
        fontWeight: 'bold',
        fontSize: '0.75rem',
        color: '#555',
        writingMode: 'sideways-lr',
        alignItems: 'center',
        justifyContent: 'center',
        display: 'flex',
        border: '1px solid #ccc',
        borderTop: ponduranceRow ? '1px solid #ccc' : 'none',
        height: ponduranceRow ? '135px' : '90px'
      };
    }
  },
  {
    field: 'metric_name',
    headerName: '',
    width: 150,
    minWidth: 150,
    suppressHeaderMenuButton: true,
    flex: 1,
    resizable: false,
    cellStyle: {
      fontWeight: 'bold',
      fontSize: '0.75rem',
      borderBottom: '1px solid #ccc',
      color: 'black'
    }
  },
  {
    field: 'p1',
    headerName: 'P1',
    width: 90,
    minWidth: 90,
    suppressHeaderMenuButton: true,
    headerStyle: {
      color: PRIORITY_COLORS.P1,
      fontWeight: 'bold'
    },
    cellStyle: { borderBottom: '1px solid #ccc', color: 'black' },
    resizable: false
  },
  {
    field: 'p2',
    headerName: 'P2',
    width: 90,
    minWidth: 90,
    suppressHeaderMenuButton: true,
    headerStyle: {
      color: PRIORITY_COLORS.P2,
      fontWeight: 'bold'
    },
    cellStyle: { borderBottom: '1px solid #ccc', color: 'black' },
    resizable: false
  },
  {
    field: 'p3',
    headerName: 'P3',
    width: 90,
    minWidth: 90,
    suppressHeaderMenuButton: true,
    headerStyle: {
      color: PRIORITY_COLORS.P3,
      fontWeight: 'bold'
    },
    cellStyle: { borderBottom: '1px solid #ccc', color: 'black' },
    resizable: false
  },
  {
    field: 'p4',
    headerName: 'P4',
    width: 90,
    minWidth: 90,
    suppressHeaderMenuButton: true,
    headerStyle: {
      color: PRIORITY_COLORS.P4,
      fontWeight: 'bold'
    },
    cellStyle: { borderBottom: '1px solid #ccc', color: 'black' },
    resizable: false
  },
  {
    field: 'p5',
    headerName: 'P5',
    width: 90,
    minWidth: 90,
    suppressHeaderMenuButton: true,
    headerStyle: { color: PRIORITY_COLORS.P5, fontWeight: 'bold' },
    cellStyle: { borderBottom: '1px solid #ccc', color: 'black' },
    resizable: false
  }
];

export const TICKET_STAGES = [
  // {
  //   label: 'Genesis',
  //   description: 'Event occurs on the originating system.'
  // },
  {
    label: 'Ingest',
    description:
      'We ingest the event in the form of the log into a Pondurance system.'
  },
  {
    label: 'Detection',
    description: 'We detect the event as a potential threat for investigation.'
  },
  {
    label: 'Claimed',
    description:
      'This is the first time an analyst sees the event and begins investigating.'
  },
  {
    label: 'Contained',
    description:
      'This is the time we took action to stop a threat via an isolation action.'
  },
  {
    label: 'Escalated',
    description:
      'This is the time we escalated the case to the client via Scope.'
  },
  {
    label: 'Acknowledged',
    description: 'This is the time you as the customer acknowledged the ticket.'
  },
  {
    label: 'Remediated',
    description: 'This is the time where the ticket was marked as remediated.'
  }
];

export const TICKET_STAGE_COLORS = {
  Genesis: '#29b6f6',
  Ingest: '#212121',
  Detection: '#78909C',
  Claimed: '#FFAB40',
  Contained: '#0097A7',
  Escalated: '#CC0000',
  Acknowledged: '#142C53',
  Remediated: '#4C1130'
};

export default REPORTS_METRICS_COLUMNS;
